import React, { useState, useEffect } from 'react';
import './TemplateForm.css';

const AVAILABLE_PLACEHOLDERS = [
  { key: 'name', label: 'Contact Name' },
  { key: 'email', label: 'Email Address' },
  { key: 'employer', label: 'Employer/Company' },
  { key: 'location', label: 'Location' },
  { key: 'education', label: 'Education' },
  { key: 'gender', label: 'Gender' },
  { key: 'address', label: 'Full Address' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'website', label: 'Website' },
  { key: 'country', label: 'Country' }
];

function TemplateForm({ template, onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    template_type: 'initial',
    is_active: true
  });

  const [bodyHtml, setBodyHtml] = useState(''); // eslint-disable-line no-unused-vars

  // Populate form if editing
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        body: template.body,
        template_type: template.template_type,
        is_active: template.is_active
      });
      setBodyHtml(template.body);
    }
  }, [template]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBodyChange = (e) => {
    const value = e.target.value;
    setBodyHtml(value);
    setFormData(prev => ({
      ...prev,
      body: value
    }));
  };

  const insertPlaceholder = (key) => {
    const textarea = document.getElementById('template-body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const placeholder = `{{${key}}}`;
    
    const newBody = formData.body.substring(0, start) + placeholder + formData.body.substring(end);
    setFormData(prev => ({ ...prev, body: newBody }));
    setBodyHtml(newBody);
    
    // Reset cursor position
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + placeholder.length;
      textarea.focus();
    }, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Template name is required');
      return;
    }
    if (!formData.subject.trim()) {
      alert('Subject is required');
      return;
    }
    if (!formData.body.trim()) {
      alert('Body content is required');
      return;
    }

    onSubmit({
      name: formData.name,
      subject: formData.subject,
      body: formData.body,
      template_type: formData.template_type,
      is_active: formData.is_active
    });
  };

  const addBasicFormatting = (tag) => {
    const textarea = document.getElementById('template-body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.body.substring(start, end) || 'text';
    
    let formatted = '';
    switch(tag) {
      case 'bold':
        formatted = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formatted = `<em>${selectedText}</em>`;
        break;
      case 'link':
        formatted = `<a href="https://example.com">${selectedText}</a>`;
        break;
      case 'heading':
        formatted = `<h2>${selectedText}</h2>`;
        break;
      case 'paragraph':
        formatted = `<p>${selectedText}</p>`;
        break;
      default:
        return;
    }
    
    const newBody = formData.body.substring(0, start) + formatted + formData.body.substring(end);
    setFormData(prev => ({ ...prev, body: newBody }));
    setBodyHtml(newBody);
  };

  return (
    <div className="template-form">
      <h2>{template ? 'Edit Template' : 'Create New Template'}</h2>

      <form onSubmit={handleSubmit}>
        {/* Name Input */}
        <div className="form-group">
          <label>Template Name *</label>
          <input
            type="text"
            name="name"
            placeholder="e.g., Initial Outreach - Software"
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            maxLength="255"
          />
          <small>A unique name to identify this template</small>
        </div>

        {/* Template Type */}
        <div className="form-row">
          <div className="form-group">
            <label>Template Type *</label>
            <select
              name="template_type"
              value={formData.template_type}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="initial">📧 Initial Outreach</option>
              <option value="followup_1">🔔 Follow-up 1</option>
              <option value="followup_2">🔔 Follow-up 2</option>
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={loading}
              />
              <span>Active</span>
            </label>
          </div>
        </div>

        {/* Subject Input */}
        <div className="form-group">
          <label>Email Subject *</label>
          <input
            type="text"
            name="subject"
            placeholder="Hi {{name}}, Check out our service..."
            value={formData.subject}
            onChange={handleChange}
            disabled={loading}
            maxLength="500"
          />
          <small>Use &#123;&#123; and &#125;&#125; around field names like &#123;&#123;name&#125;&#125; to insert dynamic content</small>
        </div>

        {/* Body Editor */}
        <div className="form-group">
          <label>Email Body (HTML) *</label>
          
          <div className="editor-toolbar">
            <div className="toolbar-group">
              <strong>Format:</strong>
              <button type="button" onClick={() => addBasicFormatting('bold')} title="Bold">
                <strong>B</strong>
              </button>
              <button type="button" onClick={() => addBasicFormatting('italic')} title="Italic">
                <em>I</em>
              </button>
              <button type="button" onClick={() => addBasicFormatting('heading')} title="Heading">
                H2
              </button>
              <button type="button" onClick={() => addBasicFormatting('paragraph')} title="Paragraph">
                P
              </button>
              <button type="button" onClick={() => addBasicFormatting('link')} title="Link">
                🔗
              </button>
            </div>

            <div className="toolbar-group">
              <strong>Placeholders:</strong>
              {AVAILABLE_PLACEHOLDERS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  title={p.label}
                  className="placeholder-btn"
                >
                  {`{{${p.key}}}`}
                </button>
              ))}
            </div>
          </div>

          <textarea
            id="template-body"
            name="body"
            value={formData.body}
            onChange={handleBodyChange}
            disabled={loading}
            placeholder="Enter HTML here (e.g. <p>Hi {{name}},</p>)"
            rows="15"
            className="html-editor"
          />
          <small>Write HTML directly. Wrap field names with &#123;&#123; and &#125;&#125; like &#123;&#123;email&#125;&#125; to create dynamic fields.</small>
        </div>

        {/* Preview Helper */}
        <div className="preview-helper">
          <details>
            <summary>📝 Sample HTML Templates</summary>
            <div className="sample-templates">
              <h4>Simple Professional Email:</h4>
              <code>{`<p>Hi {{name}},</p>
<p>We noticed your organization {{employer}} in {{location}}.</p>
<p>We specialize in helping companies like yours.</p>
<p><a href="https://example.com">Learn More</a></p>
<p>Best regards,<br/>Your Company</p>`}</code>
            </div>
          </details>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : template ? '✏️ Update Template' : '➕ Create Template'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default TemplateForm;
